/**
 * Preview list filters — status + outcome filters for compact layer lists.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowOptimizerItemPreview } from '@trafficpulse/shared';

export type PreviewStatusFilter =
  | 'all'
  | 'active'
  | 'inactive'
  | 'excluded'
  | 'no-excluded';

export type PreviewOutcomeFilter = 'all' | 'changed' | 'unchanged' | 'kept';

export interface PreviewListFilters {
  status: PreviewStatusFilter;
  outcome: PreviewOutcomeFilter;
}

export function isRuleExcludedActive(item: FlowOptimizerItemPreview): boolean {
  return Boolean(item.excluded && item.active);
}

export function previewOutcomeKind(
  item: FlowOptimizerItemPreview,
): Exclude<PreviewOutcomeFilter, 'all'> {
  if (item.passThrough) return 'kept';
  if (item.deltaPerMille === 0) return 'unchanged';
  return 'changed';
}

export function applyPreviewListFilters(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
  filters: PreviewListFilters,
): FlowOptimizerItemPreview[] {
  return items.filter(
    (item) => matchesStatus(item, filters.status) && matchesOutcome(item, filters.outcome),
  );
}

function matchesStatus(
  item: FlowOptimizerItemPreview,
  status: PreviewStatusFilter,
): boolean {
  if (status === 'all') return true;
  if (status === 'active') return item.active !== false;
  if (status === 'inactive') return item.active === false;
  if (status === 'excluded') return isRuleExcludedActive(item);
  return !isRuleExcludedActive(item);
}

function matchesOutcome(
  item: FlowOptimizerItemPreview,
  outcome: PreviewOutcomeFilter,
): boolean {
  if (outcome === 'all') return true;
  return previewOutcomeKind(item) === outcome;
}
